import React from 'react';
import { Card, Col, Row, Space, Statistic, Tag, Typography } from 'antd';
import { formatMoney } from '@/utils/format';
import { useWave5Queries, type Wave5Ctx } from './EpcEstimateWave5Panels';
import {
  EST3_PROJECT_COST_BUCKET_MAP,
  type Est3ProjectCostLeafKey,
} from './est3ProjectCostBuckets';

const { Text } = Typography;

export interface EpcEstimateProjectCostStripProps {
  ctx: Wave5Ctx;
  activeLeaf: Est3ProjectCostLeafKey;
}

/**
 * EST3 UX2 Wave 7 — compact TEC strip with active project-cost bucket highlight.
 */
export const EpcEstimateProjectCostStrip: React.FC<EpcEstimateProjectCostStripProps> = ({
  ctx,
  activeLeaf,
}) => {
  const { totalQ } = useWave5Queries(ctx);
  const total = totalQ.data;
  const bucket = EST3_PROJECT_COST_BUCKET_MAP[activeLeaf];
  const pct = total?.structure_pct || {};
  const highlightAmt = total ? Number(total[bucket.field]) || 0 : 0;

  return (
    <Card size="small" type="inner">
      <Row gutter={16} align="middle">
        <Col xs={24} md={8}>
          <Statistic
            title="Total Estimated Cost"
            value={Number(total?.total_estimated_cost) || 0}
            formatter={(v) =>
              formatMoney(v, { display: 'plain', maximumFractionDigits: 0 }) +
              (total?.currency ? ` ${total.currency}` : '')
            }
          />
        </Col>
        <Col xs={24} md={8}>
          <Statistic
            title={bucket.label}
            value={highlightAmt}
            formatter={(v) => formatMoney(v, { display: 'plain', maximumFractionDigits: 0 })}
          />
        </Col>
        <Col xs={24} md={8}>
          <Text type="secondary">Cost structure</Text>
          <Space wrap style={{ marginTop: 4 }}>
            <Tag color={activeLeaf === 'indirects' || activeLeaf === 'duration' ? 'blue' : undefined}>
              Indirect {pct.indirect ?? 0}%
            </Tag>
            <Tag color={activeLeaf === 'risks' ? 'blue' : undefined}>Risk {pct.risk ?? 0}%</Tag>
            <Tag color={activeLeaf === 'contingency' ? 'blue' : undefined}>
              Contingency {pct.contingency ?? 0}%
            </Tag>
            <Tag color={activeLeaf === 'escalation' ? 'blue' : undefined}>
              Escalation {pct.escalation ?? 0}%
            </Tag>
            <Tag color={activeLeaf === 'fx' ? 'blue' : undefined}>FX {pct.fx ?? 0}%</Tag>
          </Space>
        </Col>
      </Row>
      {(total?.warnings || []).length > 0 ? (
        <Text type="warning" style={{ display: 'block', marginTop: 8 }}>
          {(total!.warnings as any[]).map((w) => w.message).join(' · ')}
        </Text>
      ) : null}
    </Card>
  );
};
